import { Box, Button, Icon, Text, VStack } from '@aj/ui';
import { useState } from 'react';

import { GoalCompose } from '../goal/GoalCompose.js';
import { useT } from '../../providers/I18nProvider.js';

export type EmptyProps = {
  onAdd: () => void;
  onTemplate: () => void;
  /** True once a goal exists, so the destination step is not offered twice. */
  hasGoal: boolean;
};

/**
 * Day one.
 *
 * Shows no numbers and no week strip — seven empty circles read as seven
 * failures before anything has happened, and the caller hides both.
 *
 * The DESTINATION comes before the habits. A habit with no reason behind it is
 * a chore, and the if-then plan that makes habits work is specifically weak
 * when baseline motivation is low — which is what a habit with no goal is.
 * So the order here is load-bearing rather than editorial.
 */
export function Empty({ onAdd, onTemplate, hasGoal }: EmptyProps) {
  const t = useT();
  const [goalOpen, setGoalOpen] = useState(false);

  if (goalOpen) {
    return (
      <VStack gap={5} className="flex-1 px-1 py-6">
        <GoalCompose onDone={() => setGoalOpen(false)} onCancel={() => setGoalOpen(false)} />
      </VStack>
    );
  }

  return (
    <VStack gap={5} align="center" justify="center" className="flex-1 px-4 py-10 text-center">
      <Box className="grid size-16 place-items-center rounded-full bg-accent-soft text-accent-hi">
        <Icon name="bolt" size={32} />
      </Box>

      <VStack gap={2} align="center">
        <Text variant="heading">{t('home.empty.title')}</Text>
        <Text variant="body" tone="muted" className="text-pretty">
          {t('home.empty.body')}
        </Text>
      </VStack>

      <VStack gap={2} className="w-full">
        {!hasGoal && (
          <Button
            size="lg"
            icon="map"
            fullWidth
            onClick={() => setGoalOpen(true)}
            data-coach="set-goal"
          >
            {t('goal.setDestination')}
          </Button>
        )}
        <Button
          size="lg"
          icon="plus"
          variant={hasGoal ? 'primary' : 'secondary'}
          fullWidth
          onClick={onAdd}
          data-coach="add-habit"
        >
          {t('home.addHabit')}
        </Button>
        <Button size="lg" variant="gold" icon="trophy" fullWidth onClick={onTemplate}>
          {t('home.template')}
        </Button>
        <Text variant="caption" tone="subtle">
          {t('home.templateHint')}
        </Text>
      </VStack>
    </VStack>
  );
}
